import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { Loader } from '../../components/1elements';
import AuthorizationError from '../../components/2compounds/AuthorizationError';
import {
  getDashboardErrorMessage,
  useApproveDashboardMetricValueRevisionMutation,
  useListDashboardPendingReviewsQuery,
  useRejectDashboardMetricValueRevisionMutation
} from './dashboardApi';
import { canViewDashboardApprovals } from './dashboardPermissions';
import {
  DASHBOARD_REVIEW_DECISION,
  DASHBOARD_REVIEW_NOTE_MAX_LENGTH,
  formatDashboardReviewEffectiveDate,
  formatDashboardReviewTimestamp,
  formatDashboardReviewValue,
  validateDashboardReviewDecision
} from './dashboardReviewModel';

const PAGE_SIZE = 25;

const ReviewValue = ({ caption, review, revision, proposed = false }) => (
  <Box
    sx={{
      bgcolor: proposed ? 'primary.50' : 'grey.100',
      border: '1px solid',
      borderColor: proposed ? 'primary.light' : 'divider',
      borderRadius: 1,
      minHeight: 104,
      p: 2
    }}
  >
    <Typography color="text.secondary" fontWeight={600} variant="caption">
      {caption}
    </Typography>
    {revision ? (
      <>
        <Typography fontSize={24} fontWeight={700} sx={{ mt: 0.5 }}>
          {formatDashboardReviewValue(review, revision.value)}
        </Typography>
        <Typography color="text.secondary" variant="caption">
          Revision {revision.revisionNumber}
        </Typography>
      </>
    ) : (
      <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
        No approved value
      </Typography>
    )}
  </Box>
);

const DashboardReviewCard = ({ onDecision, review }) => {
  const proposedRevision = {
    revisionNumber: review.revisionNumber,
    value: review.value
  };
  const isOwnSubmission = !review.canApprove && review.canReject;

  return (
    <Paper
      component="article"
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        p: { xs: 2, md: 2.5 }
      }}
    >
      <Stack
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1}
      >
        <Box>
          <Typography component="h2" fontSize={19} fontWeight={700}>
            {review.metricLabel}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {review.locationName} · {review.dashboardName}
          </Typography>
        </Box>
        <Chip color="warning" label="Awaiting approval" size="small" />
      </Stack>

      <Typography sx={{ mt: 2 }} variant="body2">
        Effective {formatDashboardReviewEffectiveDate(review.effectiveDate)}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          mt: 1.5
        }}
      >
        <ReviewValue
          caption="Current approved value"
          review={review}
          revision={review.currentApprovedRevision}
        />
        <ReviewValue
          caption="Proposed value"
          proposed
          review={review}
          revision={proposedRevision}
        />
      </Box>

      <Stack
        alignItems={{ xs: 'flex-start', md: 'center' }}
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mt: 2 }}
      >
        <Typography color="text.secondary" variant="body2">
          Submitted by {review.submittedByName || 'Unknown submitter'} on{' '}
          {formatDashboardReviewTimestamp(review.submittedAt)}
        </Typography>
        <Stack direction="row" spacing={1}>
          {review.canReject && (
            <Button
              aria-label={`Reject ${review.metricLabel} for ${review.locationName}`}
              color="error"
              onClick={() =>
                onDecision(review, DASHBOARD_REVIEW_DECISION.REJECT)
              }
              variant="outlined"
            >
              Reject
            </Button>
          )}
          {review.canApprove && (
            <Button
              aria-label={`Approve ${review.metricLabel} for ${review.locationName}`}
              color="success"
              onClick={() =>
                onDecision(review, DASHBOARD_REVIEW_DECISION.APPROVE)
              }
              variant="contained"
            >
              Approve
            </Button>
          )}
          {!review.canApprove && !review.canReject && (
            <Chip label="View only" size="small" variant="outlined" />
          )}
        </Stack>
      </Stack>

      {isOwnSubmission && (
        <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">
          You cannot approve a value you submitted yourself.
        </Typography>
      )}
    </Paper>
  );
};

const DashboardReviewQueue = () => {
  const canView = canViewDashboardApprovals();
  const [page, setPage] = useState(1);
  const [selectedReview, setSelectedReview] = useState(null);
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const reviewsQuery = useListDashboardPendingReviewsQuery(
    { page, pageSize: PAGE_SIZE },
    { skip: !canView }
  );
  const [approveReview, approveState] =
    useApproveDashboardMetricValueRevisionMutation();
  const [rejectReview, rejectState] =
    useRejectDashboardMetricValueRevisionMutation();
  const reviews = reviewsQuery.data?.pendingReviews ?? [];
  const meta = reviewsQuery.data?.meta ?? {
    page,
    pageSize: PAGE_SIZE,
    totalItems: 0,
    totalPages: 0
  };
  const isSaving = approveState.isLoading || rejectState.isLoading;
  const actionError =
    decision === DASHBOARD_REVIEW_DECISION.APPROVE
      ? approveState.error
      : rejectState.error;

  useEffect(() => {
    const finalPage = Math.max(1, meta.totalPages);
    if (page > finalPage) setPage(finalPage);
  }, [meta.totalPages, page]);

  const openDecision = (review, nextDecision) => {
    approveState.reset?.();
    rejectState.reset?.();
    setSelectedReview(review);
    setDecision(nextDecision);
    setNote('');
    setNoteError(null);
    setSuccessMessage(null);
  };

  const closeDecision = () => {
    if (isSaving) return;
    setSelectedReview(null);
    setDecision(null);
    setNote('');
    setNoteError(null);
  };

  const submitDecision = async (event) => {
    event.preventDefault();
    const validation = validateDashboardReviewDecision(decision, note);
    setNoteError(validation.error);
    if (!validation.isValid || !selectedReview) return;

    try {
      if (decision === DASHBOARD_REVIEW_DECISION.APPROVE) {
        await approveReview({
          metricValueRevisionId: selectedReview.metricValueRevisionId,
          reviewNote: validation.note || null
        }).unwrap();
        setSuccessMessage(`${selectedReview.metricLabel} approved.`);
      } else {
        await rejectReview({
          metricValueRevisionId: selectedReview.metricValueRevisionId,
          rejectionReason: validation.note
        }).unwrap();
        setSuccessMessage(`${selectedReview.metricLabel} rejected.`);
      }
      setSelectedReview(null);
      setDecision(null);
      setNote('');
      setNoteError(null);
    } catch (_error) {
      // RTK Query exposes the normalized error through the mutation state.
    }
  };

  if (!canView) return <AuthorizationError />;
  if (reviewsQuery.isLoading) return <Loader />;

  return (
    <Box
      data-testid="dashboard-review-queue"
      sx={{
        height: '100%',
        overflowY: 'auto',
        p: { xs: 2, md: 3 },
        width: '100%'
      }}
    >
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <Stack
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              component="h1"
              fontSize={{ xs: 24, md: 30 }}
              fontWeight={700}
            >
              Dashboard data approvals
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Review customer dashboard values before they become visible.
            </Typography>
          </Box>
          <Button
            disabled={reviewsQuery.isFetching}
            onClick={() => reviewsQuery.refetch()}
            variant="outlined"
          >
            Refresh
          </Button>
        </Stack>

        {reviewsQuery.isFetching && <LinearProgress sx={{ mt: 2 }} />}
        {successMessage && (
          <Alert
            onClose={() => setSuccessMessage(null)}
            severity="success"
            sx={{ mt: 2 }}
          >
            {successMessage}
          </Alert>
        )}
        {reviewsQuery.error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <AlertTitle>Dashboard approvals could not be loaded</AlertTitle>
            {getDashboardErrorMessage(reviewsQuery.error)}
            <Box sx={{ mt: 1 }}>
              <Button onClick={() => reviewsQuery.refetch()} size="small">
                Try again
              </Button>
            </Box>
          </Alert>
        )}

        {!reviewsQuery.error && reviews.length === 0 ? (
          <Alert severity="info" sx={{ mt: 3 }}>
            <AlertTitle>No dashboard values are awaiting approval</AlertTitle>
            New values that require review will appear here.
          </Alert>
        ) : (
          !reviewsQuery.error && (
            <>
              <Typography color="text.secondary" sx={{ mt: 3 }} variant="body2">
                {meta.totalItems} {meta.totalItems === 1 ? 'value' : 'values'}{' '}
                awaiting approval
              </Typography>
              <Stack spacing={2} sx={{ mt: 1.5 }}>
                {reviews.map((review) => (
                  <DashboardReviewCard
                    key={review.metricValueRevisionId}
                    onDecision={openDecision}
                    review={review}
                  />
                ))}
              </Stack>
              {meta.totalPages > 1 && (
                <Stack alignItems="center" sx={{ mt: 3 }}>
                  <Pagination
                    count={meta.totalPages}
                    onChange={(_event, nextPage) => setPage(nextPage)}
                    page={page}
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              )}
            </>
          )
        )}
      </Box>

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={closeDecision}
        open={Boolean(selectedReview && decision)}
      >
        <Box component="form" noValidate onSubmit={submitDecision}>
          <DialogTitle>
            {decision === DASHBOARD_REVIEW_DECISION.APPROVE
              ? 'Approve dashboard value'
              : 'Reject dashboard value'}
          </DialogTitle>
          <DialogContent>
            {selectedReview && (
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Box>
                  <Typography fontWeight={700}>
                    {selectedReview.metricLabel}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {selectedReview.locationName} ·{' '}
                    {formatDashboardReviewEffectiveDate(
                      selectedReview.effectiveDate
                    )}{' '}
                    ·{' '}
                    {formatDashboardReviewValue(
                      selectedReview,
                      selectedReview.value
                    )}
                  </Typography>
                </Box>
                <TextField
                  autoFocus
                  error={Boolean(noteError)}
                  helperText={
                    noteError ||
                    `${note.length}/${DASHBOARD_REVIEW_NOTE_MAX_LENGTH}`
                  }
                  inputProps={{ maxLength: DASHBOARD_REVIEW_NOTE_MAX_LENGTH }}
                  label={
                    decision === DASHBOARD_REVIEW_DECISION.APPROVE
                      ? 'Approval note (optional)'
                      : 'Reason for rejection'
                  }
                  minRows={3}
                  multiline
                  onChange={(event) => {
                    setNote(event.target.value);
                    setNoteError(null);
                  }}
                  required={decision === DASHBOARD_REVIEW_DECISION.REJECT}
                  value={note}
                />
                {actionError && (
                  <Alert severity="error">
                    {getDashboardErrorMessage(actionError)}
                  </Alert>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSaving} onClick={closeDecision}>
              Cancel
            </Button>
            <Button
              color={
                decision === DASHBOARD_REVIEW_DECISION.APPROVE
                  ? 'success'
                  : 'error'
              }
              disabled={isSaving}
              type="submit"
              variant="contained"
            >
              {isSaving
                ? 'Saving…'
                : decision === DASHBOARD_REVIEW_DECISION.APPROVE
                  ? 'Approve value'
                  : 'Reject value'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default DashboardReviewQueue;
